import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Driver, Order, HailingStrategy, SystemConfig, OperatingContext, TextClip, SmartSplitSuggestion, AppMode, ThemeMode } from '../../types';
import { DriverMap } from './DriverMap';
import { LocateFixed, Route, ChevronDown, ChevronUp, Navigation, House, CheckCircle2, X, BellRing, Send, Share2, MessageCircle, Radio, Check, RefreshCw, Smartphone, ExternalLink, Copy, HelpCircle, Info, Headphones, MessageCircle as MessageCircleIcon, BrainCircuit, Zap, Clock, DoorOpen, Map as MapIcon } from 'lucide-react';
import { openWhatsAppText, generateHailingStrategy, shareAudioClip, handleIFoodExternalConfirmation } from '../../services/communicationService';
import { toast } from 'react-hot-toast';
import { calculateDistance } from '../../services/parserService';
import { getDriverTodayDeliveryCount } from '../../services/driverDailyProgress';
import { getDriverOrderTimestamp, getStaleDriverOrders, getVisibleDriverOrders } from '../../services/driverOrderVisibility';

// Atomic Components
import { DriverHeader } from './dashboard/DriverHeader';
import { PickupManifest } from './dashboard/PickupManifest';
import { ActiveMissionCard } from './dashboard/ActiveMissionCard';
import { ReturnToHubState } from './dashboard/ReturnToHubState';

interface DashboardProps {
    driver: Driver;
    orders: Order[];
    config: SystemConfig;
    context?: OperatingContext;
    smartSplit?: SmartSplitSuggestion | null;
    onCompleteOrder: (orderId: string, options?: { completedAt?: number; skipPerformanceIncrement?: boolean; skipExternalNotify?: boolean }) => void;
    onArrived: (orderId: string) => void;
    onConfirmPickup: () => void;
    onLogout: () => void;
    onLocationUpdate?: (driverId: string, lat: number, lng: number) => void;
    driverTheme: ThemeMode;
    onToggleDriverTheme: () => void;
    isAdmin?: boolean;
    onBackToDispatcher?: () => void;
    storeId?: string; // v50.0: Required for Tour fetching
    // v60.3: Pedidos históricos para calcular entregas do dia
    historicalOrders?: Order[];
}

export const DriverDashboard: React.FC<DashboardProps> = ({ driver, orders, config, context, smartSplit, onCompleteOrder, onArrived, onConfirmPickup, onLogout, onLocationUpdate, driverTheme, onToggleDriverTheme, isAdmin, onBackToDispatcher, storeId, historicalOrders = [] }) => {
    const [isReviewingHailing, setIsReviewingHailing] = useState(false);
    const [hailingStrategy, setHailingStrategy] = useState<HailingStrategy | null>(null);
    const [targetOrderForHailing, setTargetOrderForHailing] = useState<Order | null>(null);
    const [isProcessingHailing, setIsProcessingHailing] = useState(false);
    const [isNavigatingInternal, setIsNavigatingInternal] = useState(false);
    const [mapDisplayMode, setMapDisplayMode] = useState<'tactical' | 'recon'>('tactical');

    // v14.0: LAZY MAP STATE (ECONOMIA DRÁSTICA)
    const [isMapLoaded, setIsMapLoaded] = useState(false);

    const hailingCacheRef = useRef<Record<string, HailingStrategy>>({});

    // v56.11: useActiveTour removed - stubbed with null (feature disabled)
    const activeTour = null as any;

    const activeOrders = useMemo(() => getVisibleDriverOrders(orders), [orders]);
    const staleOrders = useMemo(() => getStaleDriverOrders(orders), [orders]);

    // v60.3: Calcular entregas do dia (CORREÇÃO DO BUG)
    // Filtra pedidos delivered pelo driver atual e pela data de hoje
    // IMPORTANTE: Usar completedDriverId em vez de driverId, pois driverId é limpo após entrega
    const todaysDeliveries = useMemo(() => {
        return getDriverTodayDeliveryCount(historicalOrders, driver);
    }, [historicalOrders, driver.id, driver.dailyDeliveriesDate, driver.dailyDeliveriesCount]);

    const resolveStaleOrder = (order: Order) => {
        const confirmed = window.confirm('Este pedido parece preso de um dia anterior. Marcar como entregue e remover da sua lista?');
        if (!confirmed) return;
        onCompleteOrder(order.id, {
            completedAt: getDriverOrderTimestamp(order) || Date.now(),
            skipPerformanceIncrement: true,
            skipExternalNotify: true,
        });
    };

    const [lastLoc, setLastLoc] = useState<{ lat: number; lng: number } | null>(null);

    useEffect(() => {
        if (!onLocationUpdate) return;
        let watchId: number;
        const startTracking = () => {
            watchId = navigator.geolocation.watchPosition(
                (pos) => {
                    const { latitude, longitude } = pos.coords;
                    setLastLoc({ lat: latitude, lng: longitude });
                    if (onLocationUpdate) onLocationUpdate(driver.id, latitude, longitude);

                    // Atualiza cache local para persistência offline
                    localStorage.setItem('sal_last_loc', JSON.stringify({ lat: latitude, lng: longitude }));
                },
                (err) => console.error("GPS Error", err),
                { enableHighAccuracy: true, maximumAge: 10000, timeout: 5000 }
            );
        };
        startTracking();
        return () => { if (watchId) navigator.geolocation.clearWatch(watchId); };
    }, [driver.id, onLocationUpdate]);

    /**
     * v50.0: ROUTE SEQUENCING ENGINE (HYBRID)
     * Priority 1: Dispatcher's Optimized Sequence (from Active Tour)
     * Priority 2: Greedy Nearest Neighbor (Legacy/Fallback/Offline)
     */
    const routeSequence = useMemo(() => {
        const currentLat = (driver.lat && driver.lat !== 0) ? driver.lat : config.businessLat;
        const currentLng = (driver.lng && driver.lng !== 0) ? driver.lng : config.businessLng;

        // 1. If we have a valid Tour with optimized legs, HONOUR IT.
        if (activeTour?.routeData?.legs && activeTour.routeData.legs.length > 0) {
            const sequenceMap = new Map<string, number>();
            activeTour.routeData.legs.forEach((leg, idx) => {
                if (leg.orderId) sequenceMap.set(leg.orderId, idx);
            });

            const optimized = activeOrders.filter(o => sequenceMap.has(o.id));
            const orphans = activeOrders.filter(o => !sequenceMap.has(o.id)); // Orders added after tour generation

            optimized.sort((a, b) => (sequenceMap.get(a.id) || 0) - (sequenceMap.get(b.id) || 0));

            // Append orphans at the end (safest fallback)
            return [...optimized, ...orphans];
        }

        // 2. FALLBACK: Greedy Nearest Neighbor (Spatial Sort)
        // Used when offline or no optimized tour exists (Legacy Behavior)
        if (activeOrders.length === 0) return [];

        const sequence: Order[] = [];
        const pool = [...activeOrders];
        let rollingLat = (lastLoc && lastLoc.lat) ? lastLoc.lat : currentLat;
        let rollingLng = (lastLoc && lastLoc.lng) ? lastLoc.lng : currentLng;

        while (pool.length > 0) {
            let nearestIdx = -1;
            let minDist = Infinity;

            for (let i = 0; i < pool.length; i++) {
                const pLat = pool[i].lat || 0;
                const pLng = pool[i].lng || 0;
                if (pLat === 0 || pLng === 0) continue;

                const d = calculateDistance(rollingLat, rollingLng, pLat, pLng);
                if (d < minDist) {
                    minDist = d;
                    nearestIdx = i;
                }
            }

            if (nearestIdx !== -1) {
                const nextOrder = pool.splice(nearestIdx, 1)[0];
                sequence.push(nextOrder);
                if (nextOrder.lat && nextOrder.lng) {
                    rollingLat = nextOrder.lat;
                    rollingLng = nextOrder.lng;
                }
            } else { sequence.push(...pool); break; }
        }
        return sequence;
    }, [driver.lat, driver.lng, activeOrders, config.businessLat, config.businessLng, lastLoc]);

    // v50.0: Geofence Automático de Chegada
    // Monitora a distância para o próximo pedido e marca como "Cheguei" se < 150m
    useEffect(() => {
        if (!routeSequence.length || !driver.lat || !driver.lng) return;

        // Pega o próximo pedido da sequência de rota (Otimizada ou Padrão)
        const targetOrder = routeSequence[0];

        if (targetOrder && !targetOrder.arrivedAt && targetOrder.lat && targetOrder.lng) {
            const distKm = calculateDistance(driver.lat, driver.lng, targetOrder.lat, targetOrder.lng);
            // Threshold: 150 metros (0.15 km)
            if (distKm <= 0.15) {
                // Debounce simples para evitar múltiplos disparos (no hook de ação já tratamos, mas aqui evita chamadas extras)
                onArrived(targetOrder.id);
            }
        }
    }, [driver.lat, driver.lng, routeSequence, onArrived]);

    const nextOrder = routeSequence[0] || null;

    return (
        <div className="min-h-screen bg-app-bg pb-28">
            <DriverHeader
                config={config}
                driver={driver}
                activeOrdersCount={activeOrders.length}
                isOnline={navigator.onLine}
                onLogout={onLogout}
                driverTheme={driverTheme}
                onToggleTheme={onToggleDriverTheme}
                isAdmin={isAdmin}
                onBackToDispatcher={onBackToDispatcher}
                // v60.3: Passando entregas do dia calculadas corretamente
                todaysDeliveries={todaysDeliveries}
            />

            {activeOrders.some(o => o.status === 'assigned' && !o.pickupConfirmed) ? (
                <PickupManifest ordersToPickup={activeOrders.filter(o => !o.pickupConfirmed)} onConfirmPickup={onConfirmPickup} onLogout={onLogout} />
            ) : (
                <>
                    {/* v14.0: LAZY MAP CONTAINER (Só paga Map Load se o motorista abrir) */}
                    {activeOrders.length > 0 && (
                        <div className="relative w-full h-[300px] bg-card-bg/80 border-b border-border-dim overflow-hidden group">
                            {isMapLoaded || isNavigatingInternal ? (
                                <DriverMap
                                    config={config} driverLat={driver.lat} driverLng={driver.lng}
                                    activeOrders={activeOrders} nextOrder={nextOrder}
                                    isStationaryAtHub={!driver.lat || driver.lat === 0}
                                    onEtaUpdate={() => { }}
                                    mapProvider={config.googleMapsApiKey ? 'google' : 'leaflet'}
                                    setMapProvider={() => { }}
                                    isNavigatingInternal={isNavigatingInternal}
                                    onStopInternalNav={() => setIsNavigatingInternal(false)}
                                    displayMode={mapDisplayMode} setDisplayMode={setMapDisplayMode}
                                    avgSpeedKmH={driver.avgSpeedKmH}
                                    speedFactor={driver.speedFactor}
                                />
                            ) : (
                                <button
                                    type="button"
                                    className="w-full h-full flex flex-col items-center justify-center gap-4 cursor-pointer hover:bg-app-bg transition-all"
                                    onClick={() => setIsMapLoaded(true)}
                                    aria-label="Carregar mapa da rota"
                                >
                                    <div className="p-5 bg-sal-cyber-teal/10 rounded-full border border-sal-cyber-teal/20 text-sal-cyber-teal group-hover:scale-110 transition-transform">
                                        <MapIcon className="w-8 h-8" />
                                    </div>
                                    <div className="text-center">
                                        <p className="text-text-primary font-black text-base uppercase tracking-[0.2em]">Visualizar no Mapa</p>
                                        <p className="text-xs text-text-muted font-bold uppercase tracking-widest mt-1">Toque para carregar a rota tática</p>
                                    </div>
                                </button>
                            )}
                        </div>
                    )}

                    <div className="max-w-3xl mx-auto px-4 mt-8">
                        {staleOrders.length > 0 && (
                            <div className="mb-6 rounded-3xl border-2 border-status-warning/40 bg-status-warning/10 p-5 shadow-xl">
                                <div className="flex items-start gap-3">
                                    <RefreshCw className="w-5 h-5 text-status-warning shrink-0 mt-0.5" />
                                    <div className="min-w-0 flex-1">
                                        <h3 className="text-sm font-black uppercase tracking-[0.2em] text-status-warning">Pedidos antigos ocultados</h3>
                                        <p className="text-xs text-text-muted font-bold uppercase tracking-wider mt-1 leading-relaxed">
                                            Encontramos {staleOrders.length} pedido(s) antigo(s) preso(s) na sua rota. Eles não contam como pendência ativa.
                                        </p>
                                        <div className="mt-4 space-y-2">
                                            {staleOrders.map(order => {
                                                const titleId = order.displayId ? `#${order.displayId}` : (order.shortCode ? `#${order.shortCode}` : `#${order.id.slice(-4)}`);
                                                return (
                                                    <div key={order.id} className="flex items-center justify-between gap-3 rounded-2xl bg-card-bg/70 border border-border-dim p-3">
                                                        <div className="min-w-0 text-left">
                                                            <div className="text-xs font-black text-text-primary uppercase truncate">{titleId} • {order.customerName}</div>
                                                            <div className="text-[10px] text-text-muted font-bold uppercase tracking-wider truncate">{order.neighborhood}</div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => resolveStaleOrder(order)}
                                                            className="shrink-0 px-3 py-2 rounded-xl bg-status-ready text-badge-text text-[10px] font-black uppercase tracking-widest active:scale-95 transition-transform"
                                                        >
                                                            Resolver
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {activeOrders.length > 0 ? (
                            <div className="space-y-6">
                                {routeSequence.map((order, idx) => (
                                    <ActiveMissionCard
                                        key={order.id}
                                        order={order}
                                        config={config}
                                        isNext={idx === 0}
                                        etaMinutes={0}
                                        isNearDestination={false}
                                        isProcessingHailing={isProcessingHailing}
                                        onCompleteOrder={onCompleteOrder}
                                        onArrived={onArrived}
                                        onStartHailing={async (o) => {
                                            setIsProcessingHailing(true);
                                            try {
                                                const strategy = await generateHailingStrategy(o, driver, context || { rainMode: 'off', isHighTraffic: false, surgeMultiplier: 1.0 }, config);
                                                setHailingStrategy(strategy);
                                                setTargetOrderForHailing(o);
                                                setIsReviewingHailing(true);
                                            } finally {
                                                setIsProcessingHailing(false);
                                            }
                                        }}
                                        onReconClick={() => { setIsMapLoaded(true); setMapDisplayMode('recon'); }}
                                        onStartInternalNav={() => { setIsMapLoaded(true); setIsNavigatingInternal(!isNavigatingInternal); }}
                                        isInternalNavActive={idx === 0 && isNavigatingInternal}
                                    />
                                ))}
                            </div>
                        ) : <ReturnToHubState driver={driver} futureOrders={[]} config={config} onLogout={onLogout} isAdmin={isAdmin} onBackToDispatcher={onBackToDispatcher} todaysDeliveries={todaysDeliveries} />}
                    </div>
                </>
            )}
        </div>
    );
};
